import "core-js/modules/es.symbol.description.js";
import "core-js/modules/web.dom-collections.iterator.js";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { LinkButton, StandardButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { getCountries } from '../../actions/countries';
import { TaxRelationType, YesNoAnswer } from '../../generated-types';
import { FetchingState } from '../../reducers/pepRca';
import { DELETED_ROW_ID } from '../../types/list-question-type';
import StandaloneYesNoQuestionType from '../../types/standalone-yes-no-question-type';
import BeneficialOwnersHelpModal from '../../util/helpModals/corporate/beneficial-owners-help-modal';
import { focusOnErrors, requiredField } from '../../util/validator';
import OverlayLoader from '../overlay-loader';
import CorporateBeneficialOwnerActorItem from './beneficial-owner-actor-item';
import { ConfirmBeneficialOwners, confirmBeneficialOwnersName, confirmedOwnersOption, rejectBeneficialOwnersCommentName } from './confirm-beneficial-owners';
const CorporateBeneficialOwnerActors = _ref => {
  let {
    submitButtonText,
    onSubmit,
    corporateBeneficialOwnerActorsSection,
    submitCorporateBeneficialOwnerActors,
    submitted,
    fetchingPepRolesState,
    fetchingRcaRolesState,
    error,
    countries,
    pepRoleTypes,
    pepRoles,
    rcaRoles,
    getPepRoles,
    getRcaRoles
  } = _ref;
  const {
    beneficialOwners,
    beneficialOwnerReasons
  } = corporateBeneficialOwnerActorsSection;
  const [beneficialOwnerRows, setBeneficialOwnerRows] = useState([]);
  const [lastUsedIndex, setLastUsedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!pepRoles && !loading) {
      setLoading(true);
      getPepRoles();
    }
  }, [pepRoles, loading, getPepRoles]);
  useEffect(() => {
    if (!rcaRoles && !loading) {
      setLoading(true);
      getRcaRoles();
    }
  }, [rcaRoles, loading, getRcaRoles]);
  useEffect(() => {
    if (!countries && !loading) {
      setLoading(true);
      getCountries();
    }
  }, [rcaRoles, loading, countries]);
  useEffect(() => {
    if (!pepRoleTypes && !loading) {
      setLoading(true);
      getPepRoles();
    }
  }, [pepRoleTypes, loading, getPepRoles]);
  useEffect(() => {
    if (submitted && !error) {
      setLoading(false);
      onSubmit();
    }
  }, [submitted, error, onSubmit]);
  useEffect(() => {
    if (fetchingPepRolesState === FetchingState.INITIAL) {
      getPepRoles();
    }
    if (fetchingRcaRolesState === FetchingState.INITIAL) {
      getRcaRoles();
    }
  }, [fetchingPepRolesState, fetchingRcaRolesState, getPepRoles, getRcaRoles]);
  const addRow = useCallback(() => {
    const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
    const newIndex = lastUsedIndex + 1;
    setLastUsedIndex(newIndex);
    const newRow = {
      id,
      index: newIndex
    };
    setBeneficialOwnerRows([...beneficialOwnerRows, newRow]);
  }, [beneficialOwnerRows, lastUsedIndex]);
  const findReasonCodeByKey = key => {
    var _beneficialOwnerReaso;
    return beneficialOwnerReasons ? (_beneficialOwnerReaso = beneficialOwnerReasons.find(reason => reason.code === key)) === null || _beneficialOwnerReaso === void 0 ? void 0 : _beneficialOwnerReaso.text : key;
  };
  const getBeneficialOwnerMainReasonsToSend = values => {
    return Object.keys(values === null || values === void 0 ? void 0 : values.beneficialOwnerReasons).filter(key => values.beneficialOwnerReasons[key] === true).map(key => {
      return {
        code: key,
        text: findReasonCodeByKey(key)
      };
    });
  };
  useEffect(() => {
    if (beneficialOwnerRows.length === 0 && beneficialOwners.length === 0) {
      addRow();
    }
    if (beneficialOwnerRows.length === 0 && beneficialOwners.length > 0) {
      let currentIndex = lastUsedIndex;
      const newRows = beneficialOwners === null || beneficialOwners === void 0 ? void 0 : beneficialOwners.map(() => {
        const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
        currentIndex += 1;
        const newRow = {
          id,
          index: currentIndex
        };
        return newRow;
      });
      setLastUsedIndex(currentIndex);
      setBeneficialOwnerRows(newRows);
    }
  }, [addRow, beneficialOwners, beneficialOwnerRows, lastUsedIndex]);
  const getCountryFromIso3 = iso3 => {
    return countries.countries.find(current => current.iso3 === iso3);
  };
  const beneficialOwnerFormDataToDto = values => {
    const corporateBeneficialOwnerActorsSection = {
      beneficialOwners,
      typeInfo: 'corporateBeneficialOwnerActorsV3'
    };
    const corporateBeneficialOwnerActorsSectionArray = [];
    if (values.hasRR === YesNoAnswer.YES) {
      for (let i = 0; i < lastUsedIndex; i += 1) {
        var _values, _Object$keys, _values2, _values3, _values4, _values5, _values6, _values9, _Object$keys2, _values10, _values11, _values12, _values13, _values15, _Object$keys3, _values16, _values17, _values18, _values19;
        const taxRelationsArray = [];
        const lastTaxIndex = ((_values = values["_".concat(i)]) === null || _values === void 0 ? void 0 : _values.taxRelations) && !!((_Object$keys = Object.keys((_values2 = values["_".concat(i)]) === null || _values2 === void 0 ? void 0 : _values2.taxRelations)) !== null && _Object$keys !== void 0 && (_Object$keys = _Object$keys.pop()) !== null && _Object$keys !== void 0 && _Object$keys.substring(1)) || 0;
        if (((_values3 = values["_".concat(i)]) === null || _values3 === void 0 ? void 0 : _values3.hasAmericanTaxRelation) === YesNoAnswer.YES) {
          let taxRelation;
          if (values["_".concat(i)].tin) {
            taxRelation = {
              country: getCountryFromIso3('USA'),
              createdDate: new Date(),
              type: TaxRelationType.WITH_TAX_NUMBER,
              taxNumber: values["_".concat(i)].tin
            };
          } else {
            taxRelation = {
              country: getCountryFromIso3('USA'),
              createdDate: new Date(),
              reasonForMissingTaxNumber: values["_".concat(i)].reasonForMissingTaxNumber,
              type: TaxRelationType.WITH_OUT_TAX_NUMBER
            };
          }
          taxRelationsArray.push(taxRelation);
        }
        if (((_values4 = values["_".concat(i)]) === null || _values4 === void 0 ? void 0 : _values4.hasInternationalTaxRelations) === YesNoAnswer.YES && (_values5 = values["_".concat(i)]) !== null && _values5 !== void 0 && _values5.taxRelations && (_values6 = values["_".concat(i)]) !== null && _values6 !== void 0 && _values6.taxRelations["_".concat(0)]) {
          for (let j = 0; j <= lastTaxIndex; j += 1) {
            var _values7, _values8;
            let taxRelation;
            if ((_values7 = values["_".concat(i)]) !== null && _values7 !== void 0 && (_values7 = _values7.taxRelations["_".concat(j)]) !== null && _values7 !== void 0 && _values7.internationalTin) {
              taxRelation = {
                country: getCountryFromIso3(values["_".concat(i)].taxRelations["_".concat(j)].internationalTaxRelationsCountry),
                createdDate: new Date(),
                type: TaxRelationType.WITH_TAX_NUMBER,
                taxNumber: values["_".concat(i)].taxRelations["_".concat(j)].internationalTin
              };
              taxRelationsArray.push(taxRelation);
            } else if ((_values8 = values["_".concat(i)]) !== null && _values8 !== void 0 && (_values8 = _values8.taxRelations["_".concat(j)]) !== null && _values8 !== void 0 && _values8.internationalTaxRelationsCountry) {
              taxRelation = {
                country: getCountryFromIso3(values["_".concat(i)].taxRelations["_".concat(j)].internationalTaxRelationsCountry),
                createdDate: new Date(),
                reasonForMissingTaxNumber: values["_".concat(i)].taxRelations["_".concat(j)].internationalReasonForMissingTaxNumber,
                type: TaxRelationType.WITH_OUT_TAX_NUMBER
              };
              taxRelationsArray.push(taxRelation);
            }
          }
        }
        const pepsArray = [];
        const lastPepIndex = ((_values9 = values["_".concat(i)]) === null || _values9 === void 0 ? void 0 : _values9.pep) && !!((_Object$keys2 = Object.keys((_values10 = values["_".concat(i)]) === null || _values10 === void 0 ? void 0 : _values10.pep)) !== null && _Object$keys2 !== void 0 && (_Object$keys2 = _Object$keys2.pop()) !== null && _Object$keys2 !== void 0 && _Object$keys2.substring(1)) || 0;
        if (((_values11 = values["_".concat(i)]) === null || _values11 === void 0 ? void 0 : _values11.isBeneficialOwnerPep) === YesNoAnswer.YES && (_values12 = values["_".concat(i)]) !== null && _values12 !== void 0 && _values12.pep && (_values13 = values["_".concat(i)]) !== null && _values13 !== void 0 && _values13.pep["_".concat(0)]) {
          for (let j = 0; j <= lastPepIndex; j += 1) {
            var _values14;
            if ((_values14 = values["_".concat(i)]) !== null && _values14 !== void 0 && (_values14 = _values14.pep["_".concat(j)]) !== null && _values14 !== void 0 && _values14.pepCountryCode) {
              const pep = {
                roleCountry: getCountryFromIso3(values["_".concat(i)].pep["_".concat(j)].pepCountryCode),
                roleTypeCode: {
                  code: values["_".concat(i)].pep["_".concat(j)].pepRelationType || ''
                }
              };
              pepsArray.push(pep);
            }
          }
        }
        const rcasArray = [];
        const lastRcaIndex = ((_values15 = values["_".concat(i)]) === null || _values15 === void 0 ? void 0 : _values15.rca) && !!((_Object$keys3 = Object.keys((_values16 = values["_".concat(i)]) === null || _values16 === void 0 ? void 0 : _values16.rca)) !== null && _Object$keys3 !== void 0 && (_Object$keys3 = _Object$keys3.pop()) !== null && _Object$keys3 !== void 0 && _Object$keys3.substring(1)) || 0;
        if (((_values17 = values["_".concat(i)]) === null || _values17 === void 0 ? void 0 : _values17.isBeneficialOwnerRca) === YesNoAnswer.YES && (_values18 = values["_".concat(i)]) !== null && _values18 !== void 0 && _values18.rca && (_values19 = values["_".concat(i)]) !== null && _values19 !== void 0 && _values19.rca["_".concat(0)]) {
          for (let j = 0; j <= lastRcaIndex; j += 1) {
            var _values20;
            if ((_values20 = values["_".concat(i)]) !== null && _values20 !== void 0 && (_values20 = _values20.rca["_".concat(j)]) !== null && _values20 !== void 0 && _values20.rcaCountryCode) {
              const rca = {
                roleCountry: getCountryFromIso3(values["_".concat(i)].rca["_".concat(j)].rcaCountryCode),
                roleTypeCode: {
                  code: values["_".concat(i)].rca["_".concat(j)].pepRelationType || ''
                },
                firstName: values["_".concat(i)].rca["_".concat(j)].rcaFirstName,
                lastName: values["_".concat(i)].rca["_".concat(j)].rcaLastName,
                relationTypeCode: {
                  code: values["_".concat(i)].rca["_".concat(j)].rcaRelationType || ''
                },
                countryOfResidence: getCountryFromIso3(values["_".concat(i)].rca["_".concat(j)].rcaCountryOfResidence)
              };
              rcasArray.push(rca);
            }
          }
        }
        const citizenshipList = [getCountryFromIso3(values["_".concat(i)].citizenship)];
        if (values["_".concat(i)].secondaryCitizenship) {
          citizenshipList.push(getCountryFromIso3(values["_".concat(i)].secondaryCitizenship));
        }
        const iso3 = values["_".concat(i)].countryOfResidence;
        corporateBeneficialOwnerActorsSectionArray.push({
          address: {
            addressLines: [values["_".concat(i)].address],
            country: getCountryFromIso3(iso3),
            postalArea: {
              value: values["_".concat(i)].postalPlace
            },
            postalCode: iso3 === 'NOR' ? {
              typeInfo: 'valid',
              code: values["_".concat(i)].postalCode,
              area: values["_".concat(i)].postalPlace
            } : {
              typeInfo: 'foreign',
              code: values["_".concat(i)].postalCode,
              country: iso3
            }
          },
          citizenship: citizenshipList,
          countryOfBirth: getCountryFromIso3(values["_".concat(i)].birthCountry),
          countryOfResidence: getCountryFromIso3(iso3),
          firstName: values["_".concat(i)].firstName,
          lastName: values["_".concat(i)].lastName,
          publicId: {
            value: values["_".concat(i)].publicId,
            token: values["_".concat(i)].token
          },
          beneficialOwnerReasons: getBeneficialOwnerMainReasonsToSend(values["_".concat(i)]),
          ownershipPercentage: values["_".concat(i)].eierandel,
          pepList: pepsArray,
          rcaList: rcasArray,
          taxRelationList: taxRelationsArray
        });
      }
    }
    corporateBeneficialOwnerActorsSection.beneficialOwners = corporateBeneficialOwnerActorsSectionArray;
    return corporateBeneficialOwnerActorsSection;
  };
  const beneficialOwnerConfirmationFormDataToDto = values => {
    const choice = values[confirmBeneficialOwnersName];
    if (choice === confirmedOwnersOption.value) return {
      typeInfo: 'CONFIRMED'
    };
    const comment = values[rejectBeneficialOwnersCommentName];
    return {
      typeInfo: 'REJECTED_WITH_COMMENT',
      comment
    };
  };
  const submitAnswers = values => {
    setLoading(true);
    submitCorporateBeneficialOwnerActors(beneficialOwnerFormDataToDto(values), beneficialOwnerConfirmationFormDataToDto(values));
  };
  const getInitialValues = () => {
    const initialValues = {};
    beneficialOwners.forEach((owner, index) => {
      var _owner$countryOfBirth, _owner$citizenship$, _owner$address, _owner$address2, _owner$address3, _owner$countryOfResid, _owner$pepList, _owner$rcaList;
      initialValues["_".concat(index)] = {
        isFromServer: true
      };
      owner.pepList.forEach((item, pepIndex) => {
        if (initialValues["_".concat(index)].pep === undefined) {
          initialValues["_".concat(index)].pep = {};
        }
        initialValues["_".concat(index)].pep["_".concat(pepIndex)] = {};
        initialValues["_".concat(index)].pep["_".concat(pepIndex)].isFromServer = true;
        initialValues["_".concat(index)].pep["_".concat(pepIndex)].pepCountryCode = item.roleCountry.iso3;
        initialValues["_".concat(index)].pep["_".concat(pepIndex)].pepRelationType = item.roleTypeCode;
      });
      owner.rcaList.forEach((item, rcaIndex) => {
        if (initialValues["_".concat(index)].rca === undefined) {
          initialValues["_".concat(index)].rca = {};
        }
        initialValues["_".concat(index)].rca["_".concat(rcaIndex)] = {};
        initialValues["_".concat(index)].rca["_".concat(rcaIndex)].isFromServer = true;
        initialValues["_".concat(index)].rca["_".concat(rcaIndex)].rcaFirstName = item.firstName;
        initialValues["_".concat(index)].rca["_".concat(rcaIndex)].rcaLastName = item.lastName;
        initialValues["_".concat(index)].rca["_".concat(rcaIndex)].rcaCountryOfResidence = item.countryOfResidence.iso3;
        initialValues["_".concat(index)].rca["_".concat(rcaIndex)].rcaCountryCode = item.roleCountry.iso3;
        initialValues["_".concat(index)].rca["_".concat(rcaIndex)].pepRelationType = item.roleTypeCode.toString();
        initialValues["_".concat(index)].rca["_".concat(rcaIndex)].rcaRelationType = item.relationTypeCode.toString();
      });
      // Build initial values for international tax relations except for USA
      owner.taxRelationList.filter(item => item.country.iso3 !== 'USA').forEach((item, taxIndex) => {
        if (initialValues["_".concat(index)].taxRelations === undefined) {
          initialValues["_".concat(index)].taxRelations = {};
        }
        initialValues["_".concat(index)].taxRelations["_".concat(taxIndex)] = {};
        initialValues["_".concat(index)].taxRelations["_".concat(taxIndex)].isFromServer = true;
        initialValues["_".concat(index)].taxRelations["_".concat(taxIndex)].internationalTaxRelationsCountry = item.country.iso3;
        if (item.type === TaxRelationType.WITH_TAX_NUMBER) {
          const tempItem = item;
          initialValues["_".concat(index)].taxRelations["_".concat(taxIndex)].internationalTin = tempItem.taxNumber;
        } else {
          const tempItem = item;
          initialValues["_".concat(index)].taxRelations["_".concat(taxIndex)].internationalReasonForMissingTaxNumber = tempItem.reasonForMissingTaxNumber;
        }
      });
      if (initialValues["_".concat(index)].taxRelations) {
        initialValues["_".concat(index)].hasInternationalTaxRelations = YesNoAnswer.YES;
      }
      const americanTaxRelation = owner.taxRelationList.find(taxRelation => taxRelation.country.iso3 === 'USA');
      if (americanTaxRelation) {
        initialValues["_".concat(index)].hasAmericanTaxRelation = YesNoAnswer.YES;
        if (americanTaxRelation.type === TaxRelationType.WITH_TAX_NUMBER) {
          const tempItem = americanTaxRelation;
          initialValues["_".concat(index)].tin = tempItem.taxNumber;
        } else {
          const tempItem = americanTaxRelation;
          initialValues["_".concat(index)].reasonForMissingTaxNumber = tempItem.reasonForMissingTaxNumber;
        }
      }
      initialValues["_".concat(index)].firstName = owner.firstName;
      initialValues["_".concat(index)].lastName = owner.lastName;
      initialValues["_".concat(index)].publicId = owner.publicId.value;
      initialValues["_".concat(index)].token = owner.publicId.token;
      initialValues["_".concat(index)].birthCountry = (_owner$countryOfBirth = owner.countryOfBirth) === null || _owner$countryOfBirth === void 0 ? void 0 : _owner$countryOfBirth.iso3;
      initialValues["_".concat(index)].citizenship = owner.citizenship[0].iso3;
      initialValues["_".concat(index)].secondaryCitizenship = (_owner$citizenship$ = owner.citizenship[1]) === null || _owner$citizenship$ === void 0 ? void 0 : _owner$citizenship$.iso3;
      initialValues["_".concat(index)].address = (_owner$address = owner.address) === null || _owner$address === void 0 ? void 0 : _owner$address.addressLines[0];
      initialValues["_".concat(index)].postalCode = (_owner$address2 = owner.address) === null || _owner$address2 === void 0 || (_owner$address2 = _owner$address2.postalCode) === null || _owner$address2 === void 0 ? void 0 : _owner$address2.code;
      initialValues["_".concat(index)].postalPlace = (_owner$address3 = owner.address) === null || _owner$address3 === void 0 || (_owner$address3 = _owner$address3.postalArea) === null || _owner$address3 === void 0 ? void 0 : _owner$address3.value;
      initialValues["_".concat(index)].countryOfResidence = (_owner$countryOfResid = owner.countryOfResidence) === null || _owner$countryOfResid === void 0 ? void 0 : _owner$countryOfResid.iso3;
      initialValues["_".concat(index)].eierandel = owner.ownershipPercentage;
      if (((_owner$pepList = owner.pepList) === null || _owner$pepList === void 0 ? void 0 : _owner$pepList.length) > 0) {
        initialValues["_".concat(index)].isBeneficialOwnerPep = YesNoAnswer.YES;
      }
      if (((_owner$rcaList = owner.rcaList) === null || _owner$rcaList === void 0 ? void 0 : _owner$rcaList.length) > 0) {
        initialValues["_".concat(index)].isBeneficialOwnerRca = YesNoAnswer.YES;
      }
    });
    if (Object.keys(initialValues).length > 0) {
      initialValues.hasRR = YesNoAnswer.YES;
    }
    return initialValues;
  };
  const initialValues = useMemo(getInitialValues, [beneficialOwners]);
  const onRemoveRow = (id, setValueOfField) => {
    const indexToRemove = beneficialOwnerRows.findIndex(row => id === row);
    if (indexToRemove >= 0) {
      const newRows = [...beneficialOwnerRows];
      newRows[indexToRemove].id = DELETED_ROW_ID;
      setBeneficialOwnerRows(newRows);
      if (setValueOfField) {
        setValueOfField({
          key: "_".concat(indexToRemove),
          value: undefined
        });
      }
    }
    setLastUsedIndex(lastUsedIndex - 1);
  };
  const rrLabels = {
    hasRR: 'Finnes det personer, alene eller sammen med nær familie, som i siste instans direkte eller indirekte eier/kontrollerer mer enn 25% av eiendelene eller stemmene i virksomheten (reelle rettighetshavere)?'
  };
  const getBeneficialOwnerQuestionLabel = () => {
    return /*#__PURE__*/React.createElement("div", null, rrLabels.hasRR, /*#__PURE__*/React.createElement(BeneficialOwnersHelpModal, null));
  };
  const getOverallRRYesNo = values => {
    const {
      hasRR
    } = values;
    if (beneficialOwners.length > 0) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "disabled-yes-no-question"
      }, getBeneficialOwnerQuestionLabel(), /*#__PURE__*/React.createElement("div", {
        className: "small-margin-left medium-margin-top"
      }, "Ja")));
    }
    return /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
      selected: hasRR,
      label: rrLabels.hasRR,
      name: "hasRR",
      validate: requiredField,
      overrideLabel: getBeneficialOwnerQuestionLabel()
    });
  };
  const submitErrorMessage = error => {
    const toSubmitError = error => {
      switch (error === null || error === void 0 ? void 0 : error.errorName) {
        case 'TOTAL_OWNERSHIP_OF_RR_IS_MORE_THAN_100_PERCENT':
          return {
            title: 'Eierandelen til reelle rettighetshavere er for høy',
            description: 'Eierandelene kan ikke overstige 100% samlet.'
          };
        default:
          return {
            title: 'Noe gikk galt!',
            description: 'Vi kunne ikke lagre svarene dine. Vennligst prøv igjen senere eller kontakt banken dersom problemene vedvarer'
          };
      }
    };
    const submitError = toSubmitError(error);
    return /*#__PURE__*/React.createElement(ErrorCard, {
      title: submitError.title,
      description: submitError.description
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, loading && !error ? /*#__PURE__*/React.createElement(OverlayLoader, null) : undefined, /*#__PURE__*/React.createElement("h2", {
    className: "header-title"
  }, "Reelle rettighetshavere"), /*#__PURE__*/React.createElement(Form, {
    onSubmit: submitAnswers,
    decorators: [focusOnErrors],
    initialValues: initialValues,
    mutators: {
      setValueOfField: (args, state, utils) => {
        utils.changeValue(state, args[0].key, () => args[0].value);
      }
    }
  }, props => {
    return /*#__PURE__*/React.createElement("form", {
      onSubmit: props.handleSubmit
    }, getOverallRRYesNo(props.values), (props.values.hasRR === YesNoAnswer.YES || beneficialOwners.length > 0) && /*#__PURE__*/React.createElement("div", {
      className: "beneficial-owner-section"
    }, beneficialOwnerRows === null || beneficialOwnerRows === void 0 ? void 0 : beneficialOwnerRows.filter(row => row.id !== DELETED_ROW_ID).map((beneficialOwner, index) => {
      var _props$values;
      return beneficialOwner.id !== DELETED_ROW_ID && /*#__PURE__*/React.createElement("div", {
        key: "rr-".concat(beneficialOwner.id)
      }, /*#__PURE__*/React.createElement("div", {
        className: "extra-margin-top rr-title"
      }, "Reell rettighetshaver #", index + 1), /*#__PURE__*/React.createElement(CorporateBeneficialOwnerActorItem, {
        onRemoveRow: () => {
          onRemoveRow(beneficialOwner, props.form.mutators.setValueOfField);
        },
        prefix: index.toString(),
        pepRelationTypes: pepRoles,
        pepAppointmentTypes: rcaRoles,
        reasons: beneficialOwnerReasons,
        isFromServer: (_props$values = props.values["_".concat(index)]) === null || _props$values === void 0 ? void 0 : _props$values.isFromServer,
        values: props.values,
        setValueOfField: props.form.mutators.setValueOfField,
        countries: countries
      }));
    }), /*#__PURE__*/React.createElement(LinkButton, {
      id: "add-more-rr",
      icon: IconTypes.PLUSS_24,
      onClick: addRow
    }, "Legg til")), error && submitErrorMessage(error), /*#__PURE__*/React.createElement(ConfirmBeneficialOwners, null), /*#__PURE__*/React.createElement(StandardButton, {
      className: "progress-next-button",
      type: "submit"
    }, submitButtonText));
  }));
};
export default CorporateBeneficialOwnerActors;